import { ref, type Ref } from 'vue'

// Generic optimistic action queue + serial sync loop. A store pushes actions;
// the engine drains them one at a time against the server. Successful actions
// stay in the stack marked synced (so an optimistic overlay can keep showing
// the confirmed state until a fresh fetch replaces it); failed actions are
// dropped and reported. Shared so future stores reuse the same machinery.
export type SyncableAction = { id: string; synced: boolean }

export type SyncEngine<A extends SyncableAction> = {
  actionStack: Ref<A[]>
  enqueue: (action: A) => void
  remove: (id: string) => void
  replace: (actions: A[]) => void
  // Re-attempt the queue. Use when the `canSync` gate flips open (e.g. back
  // online) — nothing else re-triggers a drain on its own.
  resume: () => void
}

export type SyncEngineOptions<A extends SyncableAction> = {
  performSync: (action: A) => Promise<Partial<A> | void>
  onSynced?: (action: A) => void
  onError: (action: A, error: Error) => void
  onChange?: (actions: A[]) => void
  canSync?: () => boolean
}

export function createSyncEngine<A extends SyncableAction>(
  options: SyncEngineOptions<A>,
): SyncEngine<A> {
  const actionStack = ref<A[]>([]) as Ref<A[]>
  let draining = false

  function persist() {
    options.onChange?.(actionStack.value)
  }

  async function drain() {
    if (draining) return
    if (options.canSync && !options.canSync()) return
    draining = true
    try {
      for (;;) {
        const next = actionStack.value.find((action) => !action.synced)
        if (!next) break
        try {
          const patch = await options.performSync(next)
          Object.assign(next, patch ?? {})
          next.synced = true
          options.onSynced?.(next)
        } catch (error) {
          actionStack.value = actionStack.value.filter((action) => action.id !== next.id)
          options.onError(next, error instanceof Error ? error : new Error(String(error)))
        }
        persist()
      }
    } finally {
      draining = false
    }
  }

  function enqueue(action: A) {
    actionStack.value.push(action)
    persist()
    void drain()
  }

  function remove(id: string) {
    actionStack.value = actionStack.value.filter((action) => action.id !== id)
    persist()
  }

  function replace(actions: A[]) {
    actionStack.value = actions
    persist()
    void drain()
  }

  function resume() {
    void drain()
  }

  return { actionStack, enqueue, remove, replace, resume }
}
